/**
 * Share an own layer read-only: "Allur kórinn" / sections (chips) and individual people. Recipients
 * who cannot see this sheet (its audience mask vs. their section; editors/admins always can) are
 * greyed out with the reason. Sharing needs the network (the engine rejects with the API error).
 */
import { Check, Search, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SECTIONS, type AudienceGroup } from '../../shared/audience';
import type { MemberDTO } from '../../shared/dto/members';
import type { SongFileDTO } from '../../shared/dto/library';
import { ApiFailure, NetworkFailure } from '../api/client';
import type { LayerView, SheetAnnotations } from '../annotations/contract';
import { useMembers } from '../api/members';
import { useRequiredMe } from '../auth/AuthProvider';
import { Avatar, SectionBadge } from '../components/people';
import { useToast } from '../components/toast';
import { Button, ChipGroup, cx, Sheet } from '../components/ui';
import { useT } from '../i18n';
import { useOnline } from '../lib/useOnline';
import { draftFromShares, memberCanSee, sectionCanSee, sharesFromDraft, type ShareDraft } from './share';

const collator = new Intl.Collator('is', { sensitivity: 'base' });
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export function ShareSheet({ layer, file, engine, onClose }: { layer: LayerView | null; file: SongFileDTO; engine: SheetAnnotations; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const me = useRequiredMe();
  const online = useOnline();
  const members = useMembers(layer !== null);
  const [draft, setDraft] = useState<ShareDraft>({ all: false, sections: [], users: [] });
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const mask = file.audienceMask;
  const layerId = layer?.id ?? null;

  useEffect(() => {
    if (!layer) return;
    // A section shared earlier may no longer see the sheet (its audience was narrowed): its chip is
    // disabled, so drop it here rather than keep sending a share the server refuses.
    const d = draftFromShares(layer.shares);
    setDraft({ ...d, sections: d.sections.filter((s) => sectionCanSee(mask, s)) });
    setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId]);

  const people = useMemo(() => {
    const list = (members.data ?? []).filter((m) => m.id !== me.id && (m.status === 'active' || m.status === 'leave'));
    return list.sort((a, b) => collator.compare(a.fullName, b.fullName));
  }, [members.data, me.id]);
  const q = fold(query.trim());
  const shown = q ? people.filter((m) => fold(m.fullName).includes(q)) : people;

  const groupOptions = [
    { value: 'all' as AudienceGroup, label: t('audience.all') },
    ...SECTIONS.map((s) => {
      const ok = sectionCanSee(mask, s);
      return { value: s as AudienceGroup, label: t(`section.${s}`), disabled: !ok, hint: ok ? undefined : t('annotate.share_cant_see') };
    }),
  ];
  const groupValue: AudienceGroup[] = draft.all ? ['all'] : draft.sections;
  const blockedSections = SECTIONS.filter((s) => !sectionCanSee(mask, s)).map((s) => t(`section.short.${s}`));

  const onGroups = (next: AudienceGroup[]) => {
    const added = next.filter((g) => !groupValue.includes(g));
    if (added.includes('all')) setDraft((d) => ({ ...d, all: true, sections: [] }));
    else
      setDraft((d) => ({
        ...d,
        all: false,
        sections: SECTIONS.filter((s) => next.includes(s) && sectionCanSee(mask, s)),
      }));
  };

  const toggleUser = (m: MemberDTO) =>
    setDraft((d) => ({ ...d, users: d.users.includes(m.id) ? d.users.filter((u) => u !== m.id) : [...d.users, m.id] }));

  const save = async () => {
    if (!layer) return;
    setBusy(true);
    try {
      // Only recipients that can see the sheet are sent (the server would refuse the others).
      const allowed = new Set(people.filter((m) => memberCanSee(mask, m)).map((m) => m.id));
      const shares = sharesFromDraft({
        ...draft,
        sections: draft.sections.filter((s) => sectionCanSee(mask, s)),
        users: draft.users.filter((u) => allowed.has(u) || !members.data),
      });
      await engine.shareLayer(layer.id, shares);
      toast.show(t('annotate.share_saved'), { tone: 'ok' });
      onClose();
    } catch (e) {
      const offline = e instanceof NetworkFailure || !navigator.onLine;
      toast.show(offline ? t('annotate.share_offline') : e instanceof ApiFailure && e.status === 422 ? t('annotate.share_invalid') : t('annotate.share_failed'), {
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const nothing = !draft.all && draft.sections.length === 0 && draft.users.length === 0;
  const byId = new Map(people.map((m) => [m.id, m]));
  const summary = [
    ...(draft.all ? [t('audience.all')] : draft.sections.map((s) => t(`section.short.${s}`))),
    ...draft.users.map((u) => byId.get(u)?.fullName ?? t('annotate.someone')),
  ].join(', ');

  return (
    <Sheet
      open={layer !== null}
      onClose={onClose}
      wide
      title={layer ? t('annotate.share_title', { name: layer.name }) : ''}
      footer={
        <>
          <Button variant="secondary" block onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button block icon={Check} busy={busy} disabled={!online} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 py-1">
        <p className="text-[var(--muted)]">{t('annotate.share_readonly')}</p>
        {!online ? (
          <p role="status" className="flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 font-medium text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
            <WifiOff className="size-5 shrink-0" aria-hidden />
            {t('annotate.share_offline')}
          </p>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <ChipGroup<AudienceGroup> label={t('annotate.share_groups')} options={groupOptions} value={groupValue} onChange={onGroups} />
          {blockedSections.length > 0 ? (
            <p className="text-[14px] text-[var(--muted)]">{t('annotate.share_sections_cant_see', { list: blockedSections.join(', ') })}</p>
          ) : null}
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="text-[15px] font-semibold text-[var(--ink-2)]">{t('annotate.share_people')}</h3>
          <label className="relative block">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[var(--muted)]" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('annotate.share_search')}
              aria-label={t('annotate.share_search')}
              className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] py-3 pr-4 pl-11 text-[17px] focus:border-[var(--focus)] focus:outline-none"
            />
          </label>
          {members.isPending ? <p className="px-1 text-[var(--muted)]">{t('common.loading')}</p> : null}
          {!members.isPending && shown.length === 0 ? <p className="px-1 text-[var(--muted)]">{t('annotate.share_no_people')}</p> : null}
          <ul className="flex flex-col gap-1">
            {shown.map((m) => {
              const can = memberCanSee(mask, m);
              const on = draft.users.includes(m.id) && can;
              const covered = can && !on && (draft.all || (m.section !== null && draft.sections.includes(m.section)));
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    disabled={!can}
                    onClick={() => toggleUser(m)}
                    className={cx(
                      'flex min-h-14 w-full items-center gap-3 rounded-xl px-2 text-left transition disabled:opacity-45',
                      on ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-2)]',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cx(
                        'flex size-6 shrink-0 items-center justify-center rounded-md border-2',
                        on ? 'border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--muted)]',
                      )}
                    >
                      {on ? <Check className="size-4" strokeWidth={3} /> : null}
                    </span>
                    <Avatar userId={m.id} name={m.fullName} rev={m.avatarRev} section={m.section} size={36} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{m.fullName}</span>
                      {!can ? (
                        <span className="block text-[14px] text-[var(--muted)]">{t('annotate.share_cant_see')}</span>
                      ) : covered ? (
                        <span className="block text-[14px] text-[var(--muted)]">{t('annotate.share_covered')}</span>
                      ) : null}
                    </span>
                    <SectionBadge section={m.section} title={m.title} short />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <p className="rounded-xl bg-[var(--surface-2)] px-3 py-2 text-[15px] text-[var(--ink-2)]">
          {nothing ? t('annotate.share_none') : t('annotate.layer_shared_with', { list: summary })}
        </p>
      </div>
    </Sheet>
  );
}
